
// Platform detection and class application utilities
export type PlatformType = 'ios' | 'android' | 'web';

export const getPlatform = (): PlatformType => {
  if (typeof window === 'undefined') return 'web';

  // First check if Capacitor is available and use its platform detection
  const capacitor = (window as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  if (capacitor && typeof capacitor.getPlatform === 'function') {
    const capacitorPlatform = capacitor.getPlatform();


    // Map Capacitor platform names to our types
    if (capacitorPlatform === 'ios') return 'ios';
    if (capacitorPlatform === 'android') return 'android';
    if (capacitorPlatform === 'web') return 'web';
  }

  // Fallback to user agent detection
  const userAgent = window.navigator.userAgent.toLowerCase();


  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  } else if (/android/.test(userAgent)) {
    return 'android';
  }

  return 'web';
};

export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod|android/.test(userAgent);
};

export const applyPlatformClasses = (): void => {
  if (typeof document === 'undefined') return;
  
  const platform = getPlatform();
  const mobile = isMobile();
  
  document.documentElement.classList.add(`platform-${platform}`);
  
  if (mobile) {
    document.documentElement.classList.add('platform-mobile');
  } else {
    document.documentElement.classList.add('platform-desktop');
  }
};

// Safe area detection for mobile devices
export const getSafeAreaInsets = (): { top: number; bottom: number; left: number; right: number } => {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  const style = getComputedStyle(document.documentElement);

  return {
    top: parseFloat(style.getPropertyValue('--safe-area-inset-top') || '0'),
    bottom: parseFloat(style.getPropertyValue('--safe-area-inset-bottom') || '0'),
    left: parseFloat(style.getPropertyValue('--safe-area-inset-left') || '0'),
    right: parseFloat(style.getPropertyValue('--safe-area-inset-right') || '0'),
  };
};

// Check if running in native app context
export const isNativeApp = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check for Capacitor native platform
  const capacitor = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (capacitor && typeof capacitor.isNativePlatform === 'function') {
    const isNative = capacitor.isNativePlatform();

    return isNative;
  }

  // Fallback: Check for cordova
  const hasCordova = !!(window as { cordova?: unknown }).cordova;

  return hasCordova;
};

// Check if running in iOS native app
export const isIOSNativeApp = (): boolean => {
  const isNative = isNativeApp();
  const platform = getPlatform();
  const isIOSNative = isNative && platform === 'ios';

  return isIOSNative;
};

// Check if running in Android native app
export const isAndroidNativeApp = (): boolean => {
  const isNative = isNativeApp();
  const platform = getPlatform();
  const isAndroidNative = isNative && platform === 'android';

  return isAndroidNative;
};

// Get current platform for referral tracking
export const getCurrentPlatform = (): string => {
  const platform = getPlatform();
  const native = isNativeApp();

  if (native) {
    return platform === 'ios' ? 'ios-app' : 'android-app';
  }

  return 'web';
};
